import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, useCurrency, API } from "../../App";
import UserLayout from "../../components/UserLayout";
import { Clock, Package, CheckCircle, Truck, XCircle, Download, ChevronRight, Star } from "lucide-react";
import { toast } from "sonner";
import ReviewDialog from "./ReviewDialog";
import { downloadInvoicePdf } from "../../utils/pdfDownload";

export default function UserOrders() {
  const navigate = useNavigate();
  const { symbol } = useCurrency();
  const { refreshUser } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewOrder, setReviewOrder] = useState(null);
  const [orderReviews, setOrderReviews] = useState([]);
  const [reviewedOrders, setReviewedOrders] = useState({});
  const [cancelling, setCancelling] = useState(null);

  useEffect(() => { fetchOrders(); }, []);

  const cancelOrder = async (e, orderId) => {
    e.stopPropagation();
    if (!window.confirm("Are you sure you want to cancel this order? The amount will be refunded to your wallet.")) return;
    setCancelling(orderId);
    try {
      const res = await fetch(`${API}/orders/${orderId}/cancel`, { method: "POST", credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchOrders();
        refreshUser(); // Auto-refresh wallet balance
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to cancel order");
      }
    } catch { toast.error("Failed to cancel"); }
    finally { setCancelling(null); }
  };

  const fetchOrders = async () => {
    try {
      const response = await fetch(`${API}/orders`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        const sorted = data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setOrders(sorted);
        // Check which delivered orders are fully reviewed
        const delivered = sorted.filter((o) => o.status === "delivered");
        const reviewMap = {};
        await Promise.all(delivered.map(async (order) => {
          try {
            const res = await fetch(`${API}/reviews/order/${order.order_id}`, { credentials: "include" });
            if (res.ok) {
              const reviews = await res.json();
              const reviewedItemIds = new Set(reviews.map((r) => r.item_id));
              const allReviewed = (order.items || []).length > 0 && (order.items || []).every((it) => reviewedItemIds.has(it.item_id));
              reviewMap[order.order_id] = allReviewed;
            }
          } catch {}
        }));
        setReviewedOrders(reviewMap);
      }
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  const downloadInvoice = async (e, orderId) => {
    e.stopPropagation();
    toast.info("Preparing invoice...");
    try {
      await downloadInvoicePdf("order", orderId, `Invoice-${orderId}.pdf`);
    } catch {
      toast.error("Failed to download invoice");
    }
  };

  const openReviewDialog = async (e, order) => {
    e.stopPropagation();
    try {
      const res = await fetch(`${API}/reviews/order/${order.order_id}`, { credentials: "include" });
      const reviews = res.ok ? await res.json() : [];
      setOrderReviews(reviews);
    } catch { setOrderReviews([]); }
    setReviewOrder(order);
  };

  const closeReviewDialog = (didSubmit) => {
    setReviewOrder(null);
    setOrderReviews([]);
    if (didSubmit) fetchOrders();
  };

  const statusConfig = {
    received: { icon: Clock, color: "#f59e0b", bg: "bg-yellow-50", label: "Received" },
    kitchen: { icon: Clock, color: "#f97316", bg: "bg-orange-50", label: "Kitchen" },
    preparing: { icon: Clock, color: "#3b82f6", bg: "bg-blue-50", label: "In Process" },
    ready: { icon: Package, color: "#8b5cf6", bg: "bg-purple-50", label: "Ready" },
    out_for_delivery: { icon: Truck, color: "#10b981", bg: "bg-green-50", label: "On the way" },
    pick_up: { icon: Package, color: "#d97706", bg: "bg-amber-50", label: "Pick Up" },
    delivered: { icon: CheckCircle, color: "#10b981", bg: "bg-green-50", label: "Delivered" },
    cancelled: { icon: XCircle, color: "#ef4444", bg: "bg-red-50", label: "Cancelled" },
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

  return (
    <UserLayout>
      <div className="p-4 animate-fade-in" data-testid="user-orders">
        <h1 className="text-base font-bold text-gray-900 dark:text-white mb-4">My Orders</h1>
        {loading ? (
          <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="skeleton h-24 rounded-xl"></div>)}</div>
        ) : orders.length === 0 ? (
          <div className="empty-state"><h3 className="text-sm font-bold text-gray-900 dark:text-white">No orders yet</h3><p className="text-sm text-gray-500">Your orders will appear here</p></div>
        ) : (
          <div className="space-y-3">
            {orders.map((order) => {
              const config = statusConfig[order.status] || statusConfig.received;
              const Icon = config.icon;
              return (
                <div key={order.order_id} className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl p-4 cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => navigate(`/orders/${order.order_id}`)} data-testid={`order-${order.order_id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      {order.order_number && <p className="text-sm font-bold text-green-600">{order.order_number}</p>}
                      <p className="text-[9px] text-gray-500">#{order.order_id}</p>
                    </div>
                    <div className={`flex items-center gap-1 ${config.bg} px-2 py-1 rounded-full`}>
                      <Icon size={12} style={{color: config.color}} />
                      <span className="text-[10px] font-bold" style={{color: config.color}}>{config.label}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[10px] text-gray-400">{order.items?.length || 0} items · {formatDate(order.created_at)}</p>
                      {order.user_phone && <p className="text-[9px] text-gray-500">Phone: {order.user_phone}</p>}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-extrabold text-gray-900 dark:text-white">{symbol}{order.total_amount}</span>
                      {order.status === "received" && (
                        <button onClick={(e) => cancelOrder(e, order.order_id)}
                          disabled={cancelling === order.order_id}
                          className="text-[10px] font-medium text-red-500 hover:text-red-700 bg-red-50 px-2 py-1 rounded-full"
                          data-testid={`cancel-${order.order_id}`}>
                          {cancelling === order.order_id ? "Cancelling..." : "Cancel"}
                        </button>
                      )}
                      {order.status === "delivered" && (
                        reviewedOrders[order.order_id]
                          ? <span className="text-[10px] font-medium text-gray-400 italic" data-testid={`reviewed-${order.order_id}`}>Already Reviewed</span>
                          : <button onClick={(e) => openReviewDialog(e, order)} className="p-1.5 text-amber-500 hover:text-amber-600" data-testid={`rate-${order.order_id}`} title="Rate Order"><Star size={14} /></button>
                      )}
                      <button onClick={(e) => downloadInvoice(e, order.order_id)} className="p-1.5 text-gray-400 hover:text-green-600" data-testid={`invoice-${order.order_id}`} title="Download Invoice"><Download size={14} /></button>
                      <ChevronRight size={14} className="text-gray-300" />
                    </div>
                  </div>
                  {order.total_tax > 0 && <p className="text-[9px] text-gray-400 mt-1">Subtotal: {symbol}{order.subtotal} + Tax: {symbol}{order.total_tax}</p>}
                  {order.kitchen_code && (
                    <p className="text-[10px] text-orange-600 mt-1 font-medium">
                      Kitchen: {order.kitchen_code} · {order.kitchen_city}
                      {order.order_type === "pickup" && <span className="ml-2 bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full text-[9px] font-bold" data-testid={`pickup-badge-${order.order_id}`}>Pickup</span>}
                    </p>
                  )}
                  {!order.kitchen_code && order.order_type === "pickup" && (
                    <p className="text-[10px] mt-1"><span className="bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-bold" data-testid={`pickup-badge-${order.order_id}`}>Pickup Order</span></p>
                  )}
                  {order.refund_status && (
                    <p className={`text-[10px] mt-1 font-medium ${order.refund_status === "approved" ? "text-green-600" : order.refund_status === "rejected" ? "text-red-500" : "text-yellow-600"}`}>
                      Refund: {order.refund_status.charAt(0).toUpperCase() + order.refund_status.slice(1)}
                    </p>
                  )}
                  {order.delivery_code && order.status !== "delivered" && (
                    <div className="flex items-center gap-2 mt-2 bg-gray-900 rounded-lg px-3 py-1.5" onClick={(e) => e.stopPropagation()}>
                      <span className="text-[9px] text-gray-500">{order.order_type === "pickup" ? "Pickup Code:" : "Delivery Code:"}</span>
                      <span className="text-sm font-mono font-bold text-green-400 tracking-widest" data-testid={`delivery-code-${order.order_id}`}>{order.delivery_code}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      {reviewOrder && (
        <ReviewDialog open={!!reviewOrder} onClose={closeReviewDialog} order={reviewOrder} existingReviews={orderReviews} />
      )}
    </UserLayout>
  );
}
